import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DataTableDirective } from 'angular-datatables';
import { FlashMessagesService } from 'flash-messages-angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { RouteService } from 'src/app/core/services';
import { DatatablesHelper } from 'src/app/helpers';
import { Property } from 'src/app/models';
import { AuthService } from 'src/app/services/auth.service';
import { PropertiesService } from 'src/app/services/properties.service';
import { ChallengePropertyDialogComponent } from 'src/app/shared/dialogs/challenge-property-dialog/challenge-property-dialog.component';
import { ChallengePropertyDialogData } from 'src/app/shared/dialogs/challenge-property-dialog/challenge-property-dialog.viewmodel';

type UIChallenge = {
  propertyID: string;
  userID:     string;
  date:       Date;
};

type UIProperty = Property & {
  challenged:   boolean;
  challenges:   UIChallenge[];
  ichallenged?: UIChallenge;
};

@Component({
  selector: 'app-properties-records',
  templateUrl: './properties-records.component.html',
  styleUrls: ['./properties-records.component.css']
})
export class PropertiesRecordsComponent implements OnInit, OnDestroy {
  readonly COLS_HIDDEN_BY_DEFAULT = [0, 10, 11, 13, 15, 17, 18, 21, 22];

  properties: UIProperty[] = [];
  loaded = false;

  canChallenge = false;
  showStatus = false;

  // Has to be 'any' for the Buttons extension to work,
  // the DataTables.Settings type doesn't recognize the configuration for the extensions
  dtOptions: DataTables.Settings = {};

  // We use this trigger because fetching the properties can take a while,
  // thus we ensure the data is fetched before rendering
  dtTrigger: Subject<any> = new Subject<any>();

  @ViewChild(DataTableDirective, { static: false })
  dtElement!: DataTableDirective;

  constructor(public auth: AuthService, private propertiesService: PropertiesService, private routeService: RouteService, private dialog: MatDialog, private flashMessage: FlashMessagesService) { }

  ngOnInit(): void {
    this.initTable();
    this.loadTable();
  }

  ngOnDestroy(): void {
    // Do not forget to unsubscribe the event
    this.dtTrigger.unsubscribe();
  }

  private initTable() {
    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 10,
      // enables horizontal scrolling on datatable
      scrollX: true,

      columnDefs: [
        {
          // Indexes of columns to be hidden by default
          "targets": this.COLS_HIDDEN_BY_DEFAULT,
          "visible": false
        }
      ],

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      dom: 'lBfrtip',

      order: [[1, 'desc']]
    };

    // Configure the buttons
    Object.assign(this.dtOptions, {
      buttons: [
        'colvis'
      ],
    });
  }

  private async loadTable() {
    // Get all the properties
    const query = {
      getchallenges: true
    };

    const res: any = await this.propertiesService.getAllPublicProperties(query).toPromise();

    // Assign the data
    this.properties = res.properties || [];
    this.canChallenge = res.canChallenge;

    this.toggleStatusCol();

    if (this.loaded) {
      this.rerender();
    }
    else {
      // Calling the DT trigger to manually render the table
      this.dtTrigger.next();
      this.loaded = true;
    }
  }

  private async tryReloadTable() {
    await this.loadTable().catch(() => {});
  }

  private rerender() {
    DatatablesHelper.redrawTable(this.dtElement, this.dtTrigger);
  }

  private toggleStatusCol(): void {
    const hasChallenges = this.properties.some(p => p.challenged);
    const showStatus = hasChallenges;

    const colsHidden = this.dtOptions.columnDefs![0];

    if (showStatus) {
      colsHidden.targets = _(this.COLS_HIDDEN_BY_DEFAULT).without(0).value();
    }
    else {
      colsHidden.targets = this.COLS_HIDDEN_BY_DEFAULT;
    }
  }

  async challenge(property: Property) {
    const dialogConfig = new MatDialogConfig();

    const data: ChallengePropertyDialogData = {
      property
    };

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = data;

    // Open dialog
    const dialogRef = this.dialog.open(ChallengePropertyDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then(async (res: any) => {
      if (!res) return;

      this.routeService.scrollToTop();

      const propertyID = res.propertyID;
      const notes = res.notes;

      // Register Properties
      const data: any = await this.propertiesService.challengeProperty(propertyID, notes).toPromise();

      // Reload table data
      await this.tryReloadTable();

      // Success
      if (data.success) {
        // Display success
        this.flashMessage.show(data.message || 'Challenge property requested.', {
          cssClass: 'alert-success',
          timeout: 3000
        });
      }
    });
  }

  async discardChallenge(property: UIProperty) {
    const propertyID = property._id;

    // Discard Property Challenge
    await this.propertiesService.discardChallenge(propertyID).toPromise();

    // Reload table data
    await this.tryReloadTable();
  }

  tooltipChallenging(property: UIProperty) {
    if (property.ichallenged) {
      return `Challenge request submitted on ${moment(property.ichallenged.date).format('ll')}. Please wait until it's processed by administrator.`;
    }
    else if (property.challenged) {
      return `Property has been challenged on ${moment(property.challenges[0].date).format('ll')}.`;
    }
    else {
      return '';
    }
  }

  sortDate(value: any) {
    return value ? +new Date(value) : 0;
  }
}
